import type { NormalizedChart, PalaceId } from './domain.js';

export interface ChartIntegrityIssue { path:string; code:string; message:string; }
const PALACE_IDS: readonly PalaceId[] = ['life','siblings','spouse','children','wealth','health','travel','friends','career','property','wellbeing','parents'] as const;

export function validateNormalizedChartIntegrity(chart: NormalizedChart): readonly ChartIntegrityIssue[] {
  const issues: ChartIntegrityIssue[]=[];
  if (chart.palaceOrder.length!==12 || new Set(chart.palaceOrder).size!==12 || PALACE_IDS.some((id)=>!chart.palaceOrder.includes(id))) {
    issues.push({path:'chart.palaceOrder',code:'invalid_palace_order',message:'命盤必須包含 12 個唯一標準宮位。'});
  }
  for (const id of PALACE_IDS) {
    const palace=chart.palaces[id];
    if(!palace){issues.push({path:`chart.palaces.${id}`,code:'missing_palace',message:'缺少標準宮位。'});continue;}
    if(palace.id!==id)issues.push({path:`chart.palaces.${id}.id`,code:'palace_id_mismatch',message:'宮位 key 與 palace.id 不一致。'});
    for(const star of [...palace.majorStars,...palace.minorStars,...palace.adjectiveStars]){
      if(star.palaceId!==id)issues.push({path:`chart.palaces.${id}.star.${star.id}.palaceId`,code:'star_palace_mismatch',message:'星曜 palaceId 與所在宮位不一致。'});
    }
  }
  const bodyFlags=PALACE_IDS.filter((id)=>chart.palaces[id]?.isBodyPalace);
  if(bodyFlags.length!==1)issues.push({path:'chart.bodyPalaceId',code:'invalid_body_palace_count',message:'命盤必須且只能有一個身宮。'});
  if(bodyFlags.length===1&&bodyFlags[0]!==chart.bodyPalaceId)issues.push({path:'chart.bodyPalaceId',code:'body_palace_mismatch',message:'bodyPalaceId 與宮位身宮旗標不一致。'});

  const edgeSet=new Set(chart.relations.map((edge)=>`${edge.from}|${edge.type}|${edge.to}`));
  for(const edge of chart.relations){
    if(!chart.palaces[edge.from]||!chart.palaces[edge.to])issues.push({path:`chart.relations.${edge.from}.${edge.type}.${edge.to}`,code:'relation_unknown_palace',message:'宮位關係指向不存在的宮位。'});
    if(['opposite','trine','adjacent'].includes(edge.type)&&!edgeSet.has(`${edge.to}|${edge.type}|${edge.from}`))issues.push({path:`chart.relations.${edge.from}.${edge.type}.${edge.to}`,code:'relation_not_symmetric',message:'對宮／三方／相鄰關係必須雙向一致。'});
  }
  for(const edge of chart.natalTransformations){
    if(!chart.palaces[edge.sourcePalaceId])issues.push({path:`chart.natalTransformations.${edge.sourcePalaceId}`,code:'unknown_transformation_source',message:'飛化來源宮不存在。'});
    if(!chart.palaces[edge.targetPalaceId])issues.push({path:`chart.natalTransformations.${edge.targetPalaceId}`,code:'unknown_transformation_target',message:'飛化目標宮不存在。'});
  }
  return issues;
}

export function assertNormalizedChartIntegrity(chart:NormalizedChart):void{
  const issues=validateNormalizedChartIntegrity(chart);
  if(issues.length)throw new Error(`Normalized chart integrity failed: ${issues.map((issue)=>`${issue.path}:${issue.code}`).join(', ')}`);
}
